export type FinalSaleState = "TRUE" | "FALSE" | "UNKNOWN";

export type FinalSaleFact = {
  ready: boolean;
  value: boolean | null;
  status: FinalSaleState | "PARTIAL" | "UNAVAILABLE";
  errors: string[];
  categoryId: string | number | null;
};

export type MerchantFact = {
  ready: boolean;
  status: "READY" | "PARTIAL" | "UNAVAILABLE";
  errors: string[];
  gtin: string;
  gtinStatus: string;
  gtinType: string;
  nativeMpn: string;
  mpnStatus: string;
};

type UnknownRecord = Record<string, unknown>;

function record(value: unknown): UnknownRecord | null {
  return value && typeof value === "object" ? (value as UnknownRecord) : null;
}

function text(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function errors(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === "string")
    : [];
}

export function unavailableFinalSaleFact(): FinalSaleFact {
  return {
    ready: false,
    value: null,
    status: "UNKNOWN",
    errors: ["UNAVAILABLE"],
    categoryId: null,
  };
}

export function unavailableMerchantFact(): MerchantFact {
  return {
    ready: false,
    status: "UNAVAILABLE",
    errors: ["UNAVAILABLE"],
    gtin: "",
    gtinStatus: "UNKNOWN",
    gtinType: "",
    nativeMpn: "",
    mpnStatus: "UNKNOWN",
  };
}

/** Preserve only a consistent typed final-sale result; all other inputs fail closed. */
export function normalizeFinalSaleFact(value: unknown): FinalSaleFact {
  const fact = record(value);
  if (!fact) return unavailableFinalSaleFact();
  if (fact.ready === true && fact.status === "TRUE" && fact.value === true) {
    return {
      ready: true,
      value: true,
      status: "TRUE",
      errors: errors(fact.errors),
      categoryId:
        typeof fact.categoryId === "string" ||
        typeof fact.categoryId === "number"
          ? fact.categoryId
          : null,
    };
  }
  if (fact.ready === true && fact.status === "FALSE" && fact.value === false) {
    return {
      ready: true,
      value: false,
      status: "FALSE",
      errors: errors(fact.errors),
      categoryId:
        typeof fact.categoryId === "string" ||
        typeof fact.categoryId === "number"
          ? fact.categoryId
          : null,
    };
  }
  return unavailableFinalSaleFact();
}

export function finalSaleState(value: unknown): FinalSaleState {
  const fact = normalizeFinalSaleFact(value);
  return fact.status === "TRUE" || fact.status === "FALSE"
    ? fact.status
    : "UNKNOWN";
}

/** Keep bridge identifiers only when their enclosing merchant snapshot is usable. */
export function normalizeMerchantFact(value: unknown): MerchantFact {
  const merchant = record(value);
  if (
    !merchant ||
    (merchant.status !== "READY" && merchant.status !== "PARTIAL")
  ) {
    return unavailableMerchantFact();
  }
  return {
    ready: merchant.ready === true,
    status: merchant.status,
    errors: errors(merchant.errors),
    gtin: text(merchant.gtin),
    gtinStatus: text(merchant.gtinStatus),
    gtinType: text(merchant.gtinType),
    nativeMpn: text(merchant.nativeMpn),
    mpnStatus: text(merchant.mpnStatus),
  };
}
